/**
 * Utilidad para mapear datos de recetas del backend al formato esperado por RecipeCard
 */
import { getUnsplashImageUrlSync, getMainCategory } from '../../../shared/utils/unsplashUtils';

/**
 * Mapea una receta del backend al formato esperado por RecipeCard
 * @param {Object} recipe - Receta del backend
 * @returns {Object} Datos mapeados para RecipeCard
 */
export const mapRecipeToCardData = (recipe) => {
    if (!recipe) {
        return null;
    }

    // Usar imagen del backend si existe, sino usar placeholder de Unsplash
    let image = recipe.image_url;
    if (!image) {
        // Determinar categoría principal basada en ingredientes
        const mainCategory = getMainCategory(recipe.ingredients || []);
        // Generar imagen de Unsplash basada en la categoría
        image = getUnsplashImageUrlSync(mainCategory);
    }

    // Extraer descripción de los pasos o crear una por defecto
    let description = '';
    if (recipe.steps && recipe.steps.length > 0) {
        // Tomar el primer paso como descripción (truncado)
        const firstStep = recipe.steps[0];
        description = typeof firstStep === 'string'
            ? firstStep.substring(0, 150)
            : String(firstStep).substring(0, 150);
        if (description.length >= 150) {
            description += '...';
        }
    } else if (recipe.ingredients && recipe.ingredients.length > 0) {
        // Si no hay pasos, crear descripción basada en ingredientes
        const ingredientNames = recipe.ingredients
            .slice(0, 3)
            .map(ing => ing.item || ing.name || '')
            .filter(Boolean)
            .join(', ');
        description = `Deliciosa receta con ${ingredientNames}${recipe.ingredients.length > 3 ? ' y más' : ''}.`;
    }

    // Generar tags basados en categorías de ingredientes únicas
    const uniqueCategories = new Set();
    if (recipe.ingredients) {
        recipe.ingredients.forEach(ing => {
            if (ing.category) {
                uniqueCategories.add(ing.category);
            }
        });
    }
    const tags = Array.from(uniqueCategories).slice(0, 3);

    // Calcular dificultad basada en número de ingredientes y pasos
    let difficulty = 'Medium';
    const ingredientCount = recipe.ingredients?.length || 0;
    const stepCount = recipe.steps?.length || 0;
    const totalComplexity = ingredientCount + stepCount;

    if (totalComplexity <= 5) {
        difficulty = 'Easy';
    } else if (totalComplexity >= 15) {
        difficulty = 'Hard';
    }

    // Estimar tiempo de cocción basado en número de pasos
    let cookTime = null;
    if (stepCount > 0) {
        const estimatedMinutes = Math.max(15, stepCount * 5);
        cookTime = `${estimatedMinutes} min`;
    }

    return {
        id: recipe.id,
        image,
        title: recipe.name || 'Receta sin nombre',
        description,
        cookTime,
        difficulty,
        rating: 0, // Por defecto sin rating
        tags,
        isFavorite: recipe.is_favorite || false,
        originalRecipe: recipe // Guardar receta original para referencia
    };
};

/**
 * Mapea múltiples recetas (versión síncrona con imágenes de fallback)
 * @param {Array} recipes - Array de recetas del backend
 * @returns {Array} Array de recetas mapeadas con imágenes de fallback
 */
export const mapRecipesToCardData = (recipes = []) => {
    return recipes.map(recipe => {
        if (!recipe) return null;

        // Usar imagen del backend si existe, sino usar placeholder de Unsplash
        let image = recipe.image_url;
        if (!image) {
            // Determinar categoría principal y obtener imagen
            const mainCategory = getMainCategory(recipe.ingredients || []);
            image = getUnsplashImageUrlSync(mainCategory);
        }

        // Extraer descripción de los pasos o crear una por defecto
        let description = '';
        if (recipe.steps && recipe.steps.length > 0) {
            const firstStep = recipe.steps[0];
            description = typeof firstStep === 'string'
                ? firstStep.substring(0, 150)
                : String(firstStep).substring(0, 150);
            if (description.length >= 150) {
                description += '...';
            }
        } else if (recipe.ingredients && recipe.ingredients.length > 0) {
            const ingredientNames = recipe.ingredients
                .slice(0, 3)
                .map(ing => ing.item || ing.name || '')
                .filter(Boolean)
                .join(', ');
            description = `Deliciosa receta con ${ingredientNames}${recipe.ingredients.length > 3 ? ' y más' : ''}.`;
        }

        // Generar tags basados en categorías de ingredientes únicas
        const uniqueCategories = new Set();
        if (recipe.ingredients) {
            recipe.ingredients.forEach(ing => {
                if (ing.category) {
                    uniqueCategories.add(ing.category);
                }
            });
        }
        const tags = Array.from(uniqueCategories).slice(0, 3);

        // Calcular dificultad basada en número de ingredientes y pasos
        let difficulty = 'Medium';
        const ingredientCount = recipe.ingredients?.length || 0;
        const stepCount = recipe.steps?.length || 0;
        const totalComplexity = ingredientCount + stepCount;

        if (totalComplexity <= 5) {
            difficulty = 'Easy';
        } else if (totalComplexity >= 15) {
            difficulty = 'Hard';
        }

        // Estimar tiempo de cocción basado en número de pasos
        let cookTime = null;
        if (stepCount > 0) {
            const estimatedMinutes = Math.max(15, stepCount * 5);
            cookTime = `${estimatedMinutes} min`;
        }

        return {
            id: recipe.id,
            image,
            title: recipe.name || 'Receta sin nombre',
            description,
            cookTime,
            difficulty,
            rating: 0,
            tags,
            isFavorite: recipe.is_favorite || false,
            originalRecipe: recipe
        };
    }).filter(Boolean);
};

